import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  School,
  Work,
  Psychology,
  Code,
  DataUsage,
  Science,
} from '@mui/icons-material';
import profileConfig from '../constants/profileConfig';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const expertiseIcons = {
    'Machine Learning & Deep Learning': <DataUsage />,
    'Computer Vision': <Psychology />,
    'Natural Language Processing': <Code />,
    'Data Science': <Science />,
    'Python, TensorFlow, PyTorch': <Code />,
    'Research Methodology': <Work />,
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Header Section */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h2"
              align="center"
              sx={{ mb: 2, fontWeight: 600 }}
            >
              About {profileConfig.displayName || profileConfig.fullName || ''}
            </Typography>
            <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
              {(profileConfig.title || '') + (profileConfig.location ? ' • ' + profileConfig.location : '')}
            </Typography>
          </motion.div>

          {/* Profile Section */}
          <Grid container spacing={6} sx={{ mb: 8 }}>
            <Grid item xs={12} md={4}>
              <motion.div variants={itemVariants}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                  <Avatar
                    src={profileConfig.profileImage}
                    alt={profileConfig.name}
                    sx={{
                      width: 250,
                      height: 250,
                      border: '4px solid',
                      borderColor: 'primary.main',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                    }}
                  />
                </Box>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                      Contact Information
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Email:</strong> {profileConfig.email}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Location:</strong> {profileConfig.location}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> {profileConfig.phone}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Institution:</strong> {profileConfig.institution}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>

            <Grid item xs={12} md={8}>
              <motion.div variants={itemVariants}>
                <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                  Professional Biography
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    lineHeight: 1.8,
                    fontSize: '1.1rem',
                  }}
                >
                  {profileConfig.bio || profileConfig.about || 'N/A'}
                </Typography>

                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                  Areas of Expertise
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                  {(profileConfig.expertise || []).map((skill, index) => (
                    <Chip
                      key={index}
                      label={skill || '-'}
                      icon={expertiseIcons[skill]}
                      variant="outlined"
                      color="primary"
                      sx={{
                        '&:hover': {
                          backgroundColor: 'primary.main',
                          color: 'white',
                        },
                      }}
                    />
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>

          {/* Education Timeline */}
          <motion.div variants={itemVariants}>
            <Typography variant="h4" sx={{ mb: 4, fontWeight: 600 }}>
              Education
            </Typography>
            <Timeline position="alternate">
              {(profileConfig.education || []).map((edu, index) => (
                <TimelineItem key={index}>
                  <TimelineSeparator>
                    <TimelineDot color="primary" />
                    {index < (profileConfig.education?.length || 0) - 1 && (
                      <TimelineConnector />
                    )}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Box
                      sx={{
                        p: 3,
                        backgroundColor: 'background.paper',
                        borderRadius: 2,
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {edu.degree || '-'}
                      </Typography>
                      <Typography variant="body1" color="primary" sx={{ mb: 1 }}>
                        {edu.institution || '-'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {edu.year || '-'}
                      </Typography>
                      {edu.thesis && (
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          <strong>Thesis:</strong> {edu.thesis}
                        </Typography>
                      )}
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </motion.div>

          {/* Research Philosophy */}
          <motion.div variants={itemVariants}>
            <Card sx={{ mt: 6, p: 4 }}>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
                Research Philosophy
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                  mb: 3,
                }}
              >
                My research philosophy centers around the belief that technology should serve humanity 
                by solving real-world problems. I focus on developing innovative solutions that are 
                not only technically advanced but also ethically sound and practically applicable. 
                Through interdisciplinary collaboration and rigorous methodology, I strive to create 
                impactful research that contributes to both academic knowledge and societal progress.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  lineHeight: 1.8,
                  fontSize: '1.1rem',
                }}
              >
                I am passionate about mentoring the next generation of researchers and believe in 
                the importance of open science and knowledge sharing. My goal is to make complex 
                technologies accessible and beneficial to diverse communities while maintaining the 
                highest standards of research integrity and innovation.
              </Typography>
            </Card>
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                mt: 6,
                p: 4,
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Interested in Collaboration?
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                I'm always open to discussing research opportunities, supervising students, 
                and collaborating on innovative projects. Whether you're a student looking 
                for guidance or a researcher interested in collaboration, I'd love to hear from you.
              </Typography>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About; 